/**
 * ecommerceStyles - CSS for e-commerce components.
 * Returns a CSS string to be merged into the shadow root <style> tag.
 * Follows the same pattern as Styles.generateCSS().
 */
export function generateEcommerceCSS() {
  return `

/* ═══════════════════════════════════════
   ECOMMERCE AREA
   ═══════════════════════════════════════ */


/*
 * When products are visible, expand the panel dynamically.
 * Actual height is set via inline style by EcommerceManager._expandPanelToFit().
 * When the class is removed, the panel returns to its CSS-defined default.
 */
#text-chat-panel.ttp-panel-expanded {
  transition: height 0.25s ease;
}

/*
 * Layout overrides ONLY when ecommerce/hotels overlay is active.
 * Scoped under .ttp-products-visible / .ttp-rooms-visible so the
 * normal voice CSS takes effect when products/rooms are hidden.
 *
 * voiceActiveState is a flex column: [voice-controls] [ecommerce] [conversation].
 * Ecommerce area fills the middle and scrolls; conversation panel stays at bottom.
 */
#voiceActiveState.ttp-products-visible,
#voiceActiveState.ttp-rooms-visible {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  overflow: hidden !important;
}

.ttp-products-visible > .desktop-voice-section,
.ttp-rooms-visible > .desktop-voice-section {
  flex-shrink: 1 !important;
}

/* Conversation + input always stick to bottom; ecommerce area above grows to fill. */
.ttp-products-visible > .desktop-conversation-panel,
.ttp-rooms-visible > .desktop-conversation-panel {
  flex: 0 0 auto !important;
  margin-top: auto !important;
  display: flex !important;
  flex-direction: column !important;
}

.ttp-products-visible .desktop-conversation-panel .live-transcript-collapsed,
.ttp-rooms-visible .desktop-conversation-panel .live-transcript-collapsed {
  flex: 0 0 auto !important;
  min-height: 40px !important;
  overflow: hidden !important;
}

.ttp-products-visible .desktop-conversation-panel .voice-text-input-area,
.ttp-rooms-visible .desktop-conversation-panel .voice-text-input-area {
  margin-top: auto !important;
  padding: 3px 16px !important;
}

.ttp-products-visible .desktop-conversation-panel .voice-input-hint,
.ttp-rooms-visible .desktop-conversation-panel .voice-input-hint {
  display: none !important;
}

#voiceActiveState.ttp-products-visible .desktop-voice-section {
  display: none !important;
}

#voiceActiveState.ttp-products-visible .compact-voice-section {
  display: flex !important;
}

#voiceActiveState.ttp-products-visible .desktop-conversation-panel {
  flex: 0 0 auto !important;
}

#voiceActiveState.ttp-products-visible .conversation-history {
  display: none !important;
}

#voiceActiveState.ttp-products-visible .live-transcript-collapsed {
  flex: 0 0 auto !important;
}

.voice-text-input-area {
  flex-shrink: 0 !important;
}

.ttp-products-visible .conversation-history,
.ttp-rooms-visible .conversation-history {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  overflow-y: auto !important;
}

.ttp-ecommerce-area {
  display: flex !important;
  flex-direction: column !important;
  width: 100%;
  flex: 1 1 0 !important;
  min-height: 0 !important;
  position: relative !important;
  overflow-x: hidden !important;
  overflow-y: hidden !important;
}

/* ═══════════════════════════════════════
   PRODUCT PICKER
   ═══════════════════════════════════════ */

.ttp-product-picker {
  background: rgba(255,255,255,0.04);
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,0.08);
  box-shadow: none;
  margin: 8px 10px;
  overflow-x: hidden !important;
  overflow-y: auto !important;
  animation: ttpSlideUp 0.25s ease;
  display: flex !important;
  flex-direction: column;
  flex: 1;
  min-height: 0 !important;
}

@keyframes ttpSlideUp {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}

.ttp-picker-header {
  display: flex !important;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}

.ttp-picker-title {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255,255,255,0.5);
}

.ttp-picker-close {
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(255,255,255,0.4);
  font-size: 14px;
  padding: 2px 6px;
  border-radius: 4px;
}
.ttp-picker-close:hover {
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.7);
}

/* Horizontal card layout */
.ttp-products-horizontal {
  display: flex !important;
  align-items: flex-start;
  flex-shrink: 0;
  gap: 10px;
  padding: 12px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}
.ttp-products-horizontal::-webkit-scrollbar { height: 4px; }
.ttp-products-horizontal::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
.ttp-products-horizontal::-webkit-scrollbar-track { background: transparent; }

/* Vertical list layout */
.ttp-products-vertical {
  padding: 8px 12px;
  overflow-y: auto !important;
  display: flex !important;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-height: 0 !important;
}

/* ═══════════════════════════════════════
   PRODUCT CARD (horizontal)
   ═══════════════════════════════════════ */

.ttp-product-card {
  width: 170px !important;
  flex-shrink: 0 !important;
  background: rgba(255,255,255,0.04);
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.08);
  overflow: hidden;
  transition: border-color 0.15s;
}
.ttp-product-card:hover { border-color: rgba(109,86,245,0.5); }

.ttp-product-image {
  height: 110px !important;
  background: rgba(0,0,0,0.2);
  display: flex !important;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}
.ttp-product-image img {
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  object-fit: contain !important;
  display: block !important;
}

.ttp-stock-badge {
  position: absolute;
  top: 6px; right: 6px;
  background: rgba(239,68,68,0.15);
  color: #ef4444;
  font-size: 9px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
}

.ttp-product-details {
  padding: 10px 12px 14px;
}

.ttp-product-name {
  font-size: 13px;
  font-weight: 600;
  color: #e8e4f0;
  line-height: 1.3;
  min-height: 30px;
}

.ttp-product-page-link {
  display: inline-flex;
  align-items: center;
  margin-left: 5px;
  font-size: 10px;
  color: #a78bfa;
  text-decoration: none;
  opacity: 0.7;
  vertical-align: middle;
}
.ttp-product-page-link:hover { opacity: 1; text-decoration: underline; }

.ttp-product-meta {
  font-size: 10px;
  color: rgba(255,255,255,0.35);
  margin-top: 2px;
}

.ttp-product-footer {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
}

.ttp-product-price {
  font-size: 14px;
  font-weight: 700;
  color: #e8e4f0;
}

/* ═══════════════════════════════════════
   PRODUCT COMPACT (list view)
   ═══════════════════════════════════════ */

.ttp-product-compact {
  display: flex !important;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(255,255,255,0.04);
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.08);
  transition: border-color 0.15s;
}
.ttp-product-compact:hover { border-color: rgba(109,86,245,0.5); }

.ttp-product-image-sm {
  width: 72px !important; height: 72px !important;
  border-radius: 10px;
  background: rgba(0,0,0,0.2);
  display: flex !important;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.ttp-product-image-sm img {
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  object-fit: contain !important;
  display: block !important;
}

.ttp-product-compact .ttp-product-info {
  flex: 1;
  min-width: 0;
}
.ttp-product-compact .ttp-product-name {
  min-height: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #e8e4f0;
}
.ttp-product-compact .ttp-product-action {
  text-align: right;
  flex-shrink: 0 !important;
}
.ttp-product-compact .ttp-product-price {
  font-size: 13px;
}

.ttp-out-of-stock {
  font-size: 10px;
  color: #f87171;
  font-weight: 500;
}

/* ═══════════════════════════════════════
   ADD BUTTON
   ═══════════════════════════════════════ */

.ttp-add-btn {
  padding: 6px 14px;
  border-radius: 8px;
  border: 1.5px solid rgba(109,86,245,0.6);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: #a78bfa;
  transition: all 0.15s;
}
.ttp-add-btn:hover { background: rgba(109,86,245,0.15); border-color: #a78bfa; }
.ttp-add-btn:active { transform: scale(0.95); }
.ttp-add-btn.ttp-added { background: rgba(109,86,245,0.6) !important; border-color: #7c3aed !important; color: #fff !important; }
.ttp-add-btn:disabled { cursor: default; opacity: 0.5; }
.ttp-add-btn.ttp-update-btn { background: rgba(245,158,11,0.15); border-color: rgba(245,158,11,0.5); color: #fbbf24; }
.ttp-add-btn.ttp-update-btn:hover { background: rgba(245,158,11,0.25); border-color: #fbbf24; }

.ttp-products-animating .ttp-add-btn {
  opacity: 0.4;
  pointer-events: none;
  cursor: not-allowed;
}

/* ═══════════════════════════════════════
   CART TOAST
   ═══════════════════════════════════════ */

/* Toast replaces waveform in desktop voice section */
.ttp-cart-toast {
  display: flex !important;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin: 8px 12px;
  background: rgba(16,185,129,0.1);
  border-radius: 12px;
  border: 1px solid rgba(16,185,129,0.2);
  animation: ttpToastIn 0.25s ease;
  flex-shrink: 0;
  box-shadow: none;
}

@keyframes ttpToastIn {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.ttp-cart-toast.ttp-toast-hiding {
  animation: ttpSlideDown 0.3s ease forwards;
}

@keyframes ttpSlideDown {
  to { opacity: 0; transform: translateY(-8px); height: 0; padding: 0; margin: 0; overflow: hidden; }
}

.ttp-toast-icon {
  width: 24px !important; height: 24px !important;
  border-radius: 50%;
  background: #10b981;
  color: #fff;
  display: flex !important;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.ttp-toast-content { flex: 1; min-width: 0; }
.ttp-toast-title { font-size: 12px; font-weight: 600; color: #34d399; }
.ttp-toast-product {
  font-size: 11px;
  color: rgba(52,211,153,0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ttp-toast-undo {
  background: none;
  border: 1px solid rgba(52,211,153,0.3);
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 10px;
  color: #34d399;
  cursor: pointer;
  font-weight: 600;
  flex-shrink: 0;
}
.ttp-toast-undo:hover { background: rgba(52,211,153,0.1); }

/* Fly-to-cart desktop target (when cart summary is hidden) */
.ttp-desktop-fly-target {
  position: absolute;
  bottom: 12px;
  right: 80px;
  width: 24px;
  height: 24px;
  pointer-events: none;
}

/* Fly-to-cart ripple effect */
.ttp-fly-ripple {
  position: absolute;
  left: 10px;
  top: 50%;
  width: 30px;
  height: 30px;
  margin-top: -15px;
  border-radius: 50%;
  border: 2px solid rgba(74,222,128,0.7);
  animation: ttp-ripple-expand 0.6s ease-out forwards;
  pointer-events: none;
}

@keyframes ttp-ripple-expand {
  0% { transform: scale(0.5); opacity: 0.8; }
  100% { transform: scale(3); opacity: 0; }
}

/* ═══════════════════════════════════════
   QUANTITY/WEIGHT STEPPER
   ═══════════════════════════════════════ */

.ttp-stepper {
  display: flex !important;
  align-items: center;
  gap: 2px;
  margin: 4px 0;
}

.ttp-stepper-btn {
  width: 24px !important;
  height: 24px !important;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.6);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex !important;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  transition: all 0.15s;
  flex-shrink: 0;
}

.ttp-stepper-btn:hover:not(:disabled) {
  background: rgba(255,255,255,0.12);
  border-color: rgba(255,255,255,0.25);
}

.ttp-stepper-btn:active:not(:disabled) {
  transform: scale(0.9);
}

.ttp-stepper-btn:disabled,
.ttp-stepper-disabled {
  opacity: 0.35;
  cursor: default;
}

.ttp-stepper-value {
  font-size: 12px;
  font-weight: 600;
  color: #e8e4f0;
  min-width: 36px;
  text-align: center;
  user-select: none;
}

.ttp-product-compact .ttp-stepper {
  justify-content: flex-end;
}

.ttp-product-compact .ttp-product-action {
  display: flex !important;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.ttp-product-card .ttp-stepper {
  justify-content: center;
  margin: 4px 0;
}

.ttp-product-card .ttp-product-footer {
  display: flex !important;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

/* ═══════════════════════════════════════
   VARIATION DESKTOP PICKER
   ═══════════════════════════════════════ */

.picker-relative {
  position: relative !important;
  flex: 1 1 0 !important;
  min-height: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden;
}

/* When variant picker is shown, center it horizontally and vertically */
.picker-relative.ttp-var-picker-active {
  justify-content: center !important;
  align-items: center !important;
}

.ttp-var-picker {
  background: rgba(255,255,255,0.04);
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,0.08);
  display: flex !important;
  flex-direction: column;
  animation: ttpVarFadeIn 0.2s ease;
  overflow: hidden;
  width: 100%;
  flex-shrink: 0 !important;
}

@keyframes ttpVarFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.ttp-var-header {
  display: flex !important;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}

.ttp-var-back-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(255,255,255,0.5);
  font-size: 16px;
  padding: 0;
  display: flex;
  align-items: center;
  line-height: 1;
  font-family: inherit;
}
.ttp-var-back-btn:hover { color: #e8e4f0; }

.ttp-var-title {
  font-size: 13px;
  font-weight: 700;
  color: #e8e4f0;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ttp-var-page-link {
  font-size: 11px;
  color: #a78bfa;
  text-decoration: none;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}
.ttp-var-page-link:hover { text-decoration: underline; }

.ttp-var-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 14px;
  display: flex !important;
  flex-direction: column;
  gap: 14px;
}

.ttp-var-preview {
  display: flex !important;
  gap: 12px;
  align-items: flex-start;
}

.ttp-var-preview-img {
  width: 72px !important;
  height: 72px !important;
  border-radius: 12px;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.08);
  display: flex !important;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  transition: all 0.2s ease;
}

.ttp-var-preview-img img {
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  object-fit: contain !important;
  display: block !important;
  padding: 6px;
}

.ttp-var-preview-info { flex: 1; min-width: 0; }

.ttp-var-preview-brand {
  font-size: 10px;
  color: rgba(255,255,255,0.4);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.ttp-var-preview-name {
  font-size: 14px;
  font-weight: 700;
  color: #e8e4f0;
  margin: 2px 0 4px;
  line-height: 1.2;
}

.ttp-var-preview-price {
  font-size: 18px;
  font-weight: 700;
  color: #7c3aed;
}

.ttp-var-preview-selected {
  font-size: 11px;
  color: rgba(255,255,255,0.4);
  margin-top: 3px;
}

.ttp-var-section { display: flex !important; flex-direction: column; gap: 8px; }

.ttp-var-section-label {
  font-size: 11px;
  font-weight: 700;
  color: rgba(255,255,255,0.5);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.ttp-var-swatches { display: flex !important; flex-wrap: wrap; gap: 8px; }

.ttp-var-swatch {
  display: block !important;
  width: 28px !important;
  height: 28px !important;
  border-radius: 50%;
  cursor: pointer;
  border: 3px solid transparent;
  transition: all 0.15s;
  position: relative;
  flex-shrink: 0;
}
.ttp-var-swatch:hover { transform: scale(1.1); }
.ttp-var-swatch.selected {
  border-color: #7c3aed;
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #7c3aed;
}
.ttp-var-swatch.oos {
  opacity: 0.35;
  cursor: not-allowed;
}

.ttp-var-pills { display: flex !important; flex-wrap: wrap; gap: 6px; }

.ttp-var-pill {
  padding: 5px 12px;
  border-radius: 8px;
  border: 1.5px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.04);
  font-size: 12px;
  font-weight: 600;
  color: rgba(255,255,255,0.6);
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}
.ttp-var-pill:hover:not(:disabled) { border-color: #7c3aed; color: #a78bfa; }
.ttp-var-pill.selected { border-color: #7c3aed; background: #7c3aed; color: #fff; }
.ttp-var-pill.oos { opacity: 0.35; cursor: not-allowed; text-decoration: line-through; }

.ttp-var-footer {
  padding: 10px 14px 12px;
  border-top: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}

.ttp-var-add-btn {
  width: 100%;
  padding: 10px;
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 3px 10px rgba(124,58,237,0.3);
  transition: all 0.15s;
}
.ttp-var-add-btn:hover:not(:disabled) { transform: scale(0.99); }
.ttp-var-add-btn:disabled {
  background: rgba(255,255,255,0.1);
  box-shadow: none;
  cursor: not-allowed;
  color: rgba(255,255,255,0.3);
}

.ttp-var-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: ttpVarSpin 0.7s linear infinite;
}
@keyframes ttpVarSpin {
  to { transform: rotate(360deg); }
}

/* Amber "Choose" button for variant products */
.ttp-add-btn.ttp-choose-btn {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  box-shadow: 0 2px 8px rgba(245,158,11,0.3);
  color: #fff;
}

  `;
}
